import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Users, Trash2, Mail, MessageSquare, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { z } from "zod";
import { sanitizeError } from "@/lib/errorUtils";

const recipientSchema = z.object({
  name: z.string().min(2).max(100),
  type: z.enum(["board", "team", "custom"]),
  email: z.string().email().optional().or(z.literal("")),
  whatsapp: z.string().optional().or(z.literal("")),
  sms: z.string().optional().or(z.literal("")),
});

interface Recipient { id: string; name: string; type: string; email: string | null; whatsapp: string | null; sms: string | null; active: boolean; }

const Recipients = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [form, setForm] = useState({ name: "", type: "board" as "board" | "team" | "custom", email: "", whatsapp: "", sms: "" });

  useEffect(() => { loadRecipients(); }, [id]);

  const loadRecipients = async () => {
    try {
      const [recipientsRes, companyRes] = await Promise.all([
        supabase.from("recipients").select("*").eq("company_id", id).order("name"),
        supabase.from("companies").select("name").eq("id", id).single(),
      ]);
      if (recipientsRes.error) throw recipientsRes.error;
      setRecipients(recipientsRes.data || []);
      setCompanyName(companyRes.data?.name || "");
    } catch (error: unknown) {
      toast.error(sanitizeError(error, "Failed to load recipients"));
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const validated = recipientSchema.parse(form);
      const { error } = await supabase.from("recipients").insert([{
        company_id: id!, name: validated.name, type: validated.type,
        email: validated.email || null, whatsapp: validated.whatsapp || null, sms: validated.sms || null,
      }]);
      if (error) throw error;
      toast.success("Recipient added");
      setDialogOpen(false);
      setForm({ name: "", type: "board", email: "", whatsapp: "", sms: "" });
      loadRecipients();
    } catch (err: any) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error(err.message || "Failed to add recipient");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (recipientId: string) => {
    if (!confirm("Remove this recipient?")) return;
    try {
      const { error } = await supabase.from("recipients").delete().eq("id", recipientId);
      if (error) throw error;
      toast.success("Recipient removed");
      loadRecipients();
    } catch (error: unknown) { toast.error(sanitizeError(error, "Failed to remove")); }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "board": return "bg-primary/10 text-primary";
      case "team": return "bg-success/10 text-success";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <AppShell title="Recipients" subtitle={companyName} showBack backTo={`/app/companies/${id}`} companyId={id} companyName={companyName}>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm">Manage who receives weekly KPI summaries</p>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto rounded-xl"><Plus className="h-4 w-4 mr-2" />Add recipient</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add recipient</DialogTitle>
                  <DialogDescription>Add someone to receive weekly summaries</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2"><Label>Name</Label><Input placeholder="John Doe" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={form.type} onValueChange={(v: any) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="board">Board member</SelectItem><SelectItem value="team">Team member</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Email (optional)</Label><Input type="email" placeholder="john@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>WhatsApp (optional)</Label><Input placeholder="+91 98765 43210" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>SMS (optional)</Label><Input placeholder="+91 98765 43210" value={form.sms} onChange={(e) => setForm({ ...form, sms: e.target.value })} className="rounded-xl" /></div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-xl">Cancel</Button>
                    <Button type="submit" disabled={submitting} className="flex-1 rounded-xl">{submitting ? "Adding..." : "Add"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : recipients.length === 0 ? (
            <Card className="rounded-2xl"><CardContent className="py-12 text-center"><Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="text-lg font-semibold mb-2">No recipients yet</h3><p className="text-muted-foreground mb-6">Add recipients to start sending summaries</p><Button onClick={() => setDialogOpen(true)} className="rounded-xl"><Plus className="h-4 w-4 mr-2" />Add first recipient</Button></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {recipients.map((r) => (
                <Card key={r.id} className="rounded-2xl">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1 min-w-0">
                        <CardTitle className="text-base truncate">{r.name}</CardTitle>
                        <span className={`text-xs px-2 py-1 rounded-full ${getTypeBadgeColor(r.type)}`}>{r.type}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2 text-sm">
                      {r.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /><span className="truncate">{r.email}</span></div>}
                      {r.whatsapp && <div className="flex items-center gap-2 text-muted-foreground"><MessageSquare className="h-4 w-4" /><span>{r.whatsapp}</span></div>}
                      {r.sms && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-4 w-4" /><span>{r.sms}</span></div>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default Recipients;
